import { DotsThreeVertical } from '@phosphor-icons/react';
import { useSelector } from 'react-redux';
import { selectTherapistId, selectIsAdmin } from '../../store/slices/authSlice';
import {
  getAppointmentStatusInfo,
  getConfirmationState,
  resolveAppointmentColor,
  hexToRgba,
} from '../../utils/appointmentDisplay';
import './AppointmentCard.css';

function AppointmentCard({
  appointment,
  onClick,
  onContextMenu,
  compact = false,
  slotHeight = 60,
  showTherapistName = false,
  multilineTherapist = false,
  therapistColor = null,
  colorMode = 'status',
  treatmentTypeMap = null,
  dimmed = false,
}) {
  const startTime = new Date(appointment.startTime);
  const endTime = new Date(appointment.endTime);

  // Blocked time (חסימת זמן): cause/comments are private — visible only to the
  // owning therapist and clinic admins. Everyone else sees the generic label.
  const viewerTherapistId = useSelector(selectTherapistId);
  const viewerIsAdmin = useSelector(selectIsAdmin);
  const isBlocked = appointment.isBlockedTime === true;
  const canSeeBlockDetails =
    isBlocked && (viewerIsAdmin || viewerTherapistId === appointment.therapistId);

  const formatTime = (date) => {
    return date.toLocaleTimeString('he-IL', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const status = getAppointmentStatusInfo(appointment);
  const StatusIcon = status.icon;
  // Clinic color mode. In "treatmentType" mode the block takes the treatment
  // type's color and the confirmation state becomes a small text badge;
  // blocked/cancelled still resolve to their status look (kind === "status").
  const colorInfo = resolveAppointmentColor({ appointment, mode: colorMode, treatmentTypeMap });
  const isTypeColored = colorInfo.kind === 'type';
  const colorClass = isTypeColored
    ? `color-mode-type${colorInfo.fallback ? ' type-fallback' : ''}`
    : `status-${colorInfo.statusColorKey}`;
  const dimClass = dimmed ? ' apt-dimmed' : '';
  const typeStyle = isTypeColored
    ? { '--apt-type-color': colorInfo.hex, '--apt-type-bg': hexToRgba(colorInfo.hex, 0.16) }
    : null;
  const confirm = isTypeColored ? getConfirmationState(appointment) : null;
  const confirmBadge = (extraClass = '') =>
    confirm ? (
      <span className={`apt-confirm-badge confirm-${confirm.key} ${extraClass}`.trim()} aria-label={confirm.label}>
        {confirm.label}
      </span>
    ) : null;
  // Flag used to visually mute + strike through the card (see `.appointment-card.cancelled`).
  const isCancelled = appointment.status === 'cancelled';

  // Build the "cancelled ..." prefix used in the tooltip. When the therapist
  // entered a reason during the cancel flow we append it so hovering a muted
  // card reveals context without opening the modal.
  const cancelledTooltipPrefix = () => {
    if (!isCancelled) return '';
    const reason = appointment.cancellationReason?.trim();
    return reason ? `פגישה בוטלה — סיבה: ${reason}` : 'פגישה בוטלה';
  };

  const seriesLabel = appointment.recurringIndex && appointment.recurringTotalCount
    ? `(${appointment.recurringIndex}/${appointment.recurringTotalCount})`
    : '';

  // Owner/admin see the block cause inline; comments only in the tooltip.
  const patientDisplayName =
    canSeeBlockDetails && appointment.blockCause
      ? `זמן חסום · ${appointment.blockCause}`
      : appointment.patientName;
  const blockTooltipSuffix =
    canSeeBlockDetails && appointment.blockComments
      ? `\n${appointment.blockComments}`
      : '';

  // Calculate height based on duration - proportional to slot height
  const duration = (endTime - startTime) / (1000 * 60);
  const calculatedHeight = Math.max((duration / 60) * (slotHeight - 4), 24);

  const hasTherapist = showTherapistName && !!appointment.therapistName;

  if (compact) {
    const compactHeight = Math.max((duration / 60) * (slotHeight - 2), 18);

    const seriesTooltip = seriesLabel ? ` ${seriesLabel}` : '';
    const tooltipText = (hasTherapist
      ? `${appointment.therapistName} | ${patientDisplayName}${seriesTooltip} - ${formatTime(startTime)}${appointment.uniqueId ? `\nID: ${appointment.uniqueId}` : ''}`
      : appointment.uniqueId
        ? `${patientDisplayName}${seriesTooltip} - ${formatTime(startTime)}\nID: ${appointment.uniqueId}`
        : `${patientDisplayName}${seriesTooltip} - ${formatTime(startTime)}`) + blockTooltipSuffix;

    // Two-line layout: therapist name on top, patient name + time below.
    // Used in the clinic-wide calendar (ClinicCalendarModal) so admins can
    // scan "who is meeting whom" at a glance.
    if (hasTherapist && multilineTherapist) {
      // Two-line layout needs more vertical room than single-line. Clamp up
      // so even 30-min slots remain legible.
      const multilineHeight = Math.max(compactHeight, 34);
      return (
        <div
          className={`appointment-card compact multiline ${colorClass}${isCancelled ? ' cancelled' : ''}${dimClass}`}
          onClick={onClick}
          onContextMenu={onContextMenu}
          title={isCancelled ? `${cancelledTooltipPrefix()}\n${tooltipText}` : tooltipText}
          style={{ height: `${multilineHeight}px`, ...typeStyle }}
        >
          <div className="card-top-row">
            {therapistColor && (
              <span
                className="card-therapist-color-dot"
                style={{ backgroundColor: therapistColor }}
                aria-hidden="true"
              />
            )}
            <span className="card-therapist-name">{appointment.therapistName}</span>
            {confirm ? confirmBadge('inline') : (
              <span className={`status-indicator status-${status.color}`}>
                <StatusIcon size={10} weight="bold" />
              </span>
            )}
          </div>
          <div className="card-bottom-row">
            <span className="card-time">{`${formatTime(startTime)}-${formatTime(endTime)}`}</span>
            <span className="card-name">
              {patientDisplayName}
              {seriesLabel && <span className="card-series-tag">{seriesLabel}</span>}
            </span>
            <button
              className="card-menu-btn"
              onClick={(e) => { e.stopPropagation(); onContextMenu?.(e); }}
              title="אפשרויות"
            >
              <DotsThreeVertical size={14} weight="bold" />
            </button>
          </div>
        </div>
      );
    }

    const displayName = hasTherapist
      ? `${appointment.therapistName} | ${patientDisplayName}`
      : patientDisplayName;

    // Two-line layout (name on top, time below) so the full column width is
    // available for the patient name. Narrow desktop columns and mobile cells
    // both benefit — name fits or falls back to a single ellipsis.
    return (
      <div
        className={`appointment-card compact ${colorClass}${isCancelled ? ' cancelled' : ''}${dimClass}`}
        onClick={onClick}
        onContextMenu={onContextMenu}
        title={isCancelled ? `${cancelledTooltipPrefix()}\n${tooltipText}` : tooltipText}
        style={{ height: `${compactHeight}px`, ...typeStyle }}
      >
        <div className="card-top-row">
          <span className="card-name">
            {displayName}
            {seriesLabel && <span className="card-series-tag">{seriesLabel}</span>}
          </span>
          <button
            className="card-menu-btn"
            onClick={(e) => { e.stopPropagation(); onContextMenu?.(e); }}
            title="אפשרויות"
          >
            <DotsThreeVertical size={14} weight="bold" />
          </button>
        </div>
        <div className="card-bottom-row">
          <span className="card-time">{`${formatTime(startTime)}-${formatTime(endTime)}`}</span>
        </div>
        {confirm ? confirmBadge() : (
          <span className={`status-indicator status-${status.color}`} aria-label={status.label}>
            <StatusIcon size={8} weight="bold" />
          </span>
        )}
      </div>
    );
  }

  const tooltipText = (hasTherapist
    ? `${appointment.therapistName} | ${patientDisplayName}${seriesLabel ? ` ${seriesLabel}` : ''} - ${formatTime(startTime)}${appointment.uniqueId ? `\nID: ${appointment.uniqueId}` : ''}`
    : appointment.uniqueId
      ? `${patientDisplayName}${seriesLabel ? ` ${seriesLabel}` : ''} - ${formatTime(startTime)}\nID: ${appointment.uniqueId}`
      : `${patientDisplayName}${seriesLabel ? ` ${seriesLabel}` : ''} - ${formatTime(startTime)}`) + blockTooltipSuffix;

  // Non-compact (DayView) rendering. When `showTherapistName` is set, prepend a
  // small therapist row so the clinic calendar's day view stays readable too.
  return (
    <div
      className={`appointment-card ${colorClass}${isCancelled ? ' cancelled' : ''}${hasTherapist ? ' with-therapist' : ''}${dimClass}`}
      onClick={onClick}
      onContextMenu={onContextMenu}
      title={isCancelled ? `${cancelledTooltipPrefix()}\n${tooltipText}` : tooltipText}
      style={{ height: `${calculatedHeight}px`, ...typeStyle }}
    >
      {hasTherapist && (
        <div className="card-therapist-row">
          {therapistColor && (
            <span
              className="card-therapist-color-dot"
              style={{ backgroundColor: therapistColor }}
              aria-hidden="true"
            />
          )}
          <span className="card-therapist-name">{appointment.therapistName}</span>
        </div>
      )}
      <div className="card-main-row">
        <span className="card-time-range">{`${formatTime(startTime)}-${formatTime(endTime)}`}</span>
        <span className="card-name">{patientDisplayName} {seriesLabel && <span className="card-series-tag">{seriesLabel}</span>}</span>
        {confirm ? confirmBadge('inline') : (
          <span className={`status-badge status-${status.color}`}>
            <StatusIcon size={12} weight="bold" />
          </span>
        )}
        <button
          className="card-menu-btn"
          onClick={(e) => { e.stopPropagation(); onContextMenu?.(e); }}
          title="אפשרויות"
        >
          <DotsThreeVertical size={16} weight="bold" />
        </button>
      </div>
    </div>
  );
}

export default AppointmentCard;
